import { inject, Injectable } from '@angular/core';
import { LogsService } from '@services/logs';
import { Observable, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { LocalitySearchService } from '../locality-search';
import { LocalitySearch } from '../locality-search/models';
import { GeoCoordinates, GeolocationSearchQueryParams } from './models';

@Injectable({ providedIn: 'root' })
export class StoreLocatorService {
  private readonly geocodingService = inject(LocalitySearchService);
  private readonly logsService = inject(LogsService);

  /**
   * Retrieves the geolocation information for a given search location.
   *
   * @param {string} searchLocation - The location string for which geolocation data is to be retrieved.
   *                                  It should be at least 3 characters long; otherwise, no request is made.
   * @param {boolean} shouldIncludeAddressDetails - Whether the result labels should contain country and zipcode for dataGouv API
   */
  getLocation(searchLocation: string, shouldIncludeAddressDetails = true): Observable<LocalitySearch[] | null> {
    if (searchLocation && searchLocation.length < 3) {
      this.logsService.debug('StoreLocatorService', 'Search location is too short, not requesting location');
      return of(null);
    }

    const isPostalCode = /^\d{5}$/.test(searchLocation);
    const query: GeolocationSearchQueryParams = {
      q: searchLocation,
      autocomplete: '1',
      type: 'municipality',
      limit: isPostalCode ? '10' : '5',
    };

    this.logsService.debug('StoreLocatorService', 'Requesting location', {
      searchLocation: searchLocation || 'all stores',
      query,
    });

    return this.geocodingService.getByDataGouv(query, shouldIncludeAddressDetails).pipe(
      switchMap((dataGouvResults) => {
        if (!dataGouvResults || dataGouvResults.length === 0) {
          this.logsService.debug(
            'StoreLocatorService',
            'No usable DataGouv result, falling back to Google Autocomplete',
            { searchLocation }
          );
          return this.geocodingService.getByGoogle(query, shouldIncludeAddressDetails);
        }

        if (dataGouvResults.length < 5) {
          this.logsService.debug('StoreLocatorService', 'Less than 5 DataGouv results, fetching Google to supplement', {
            searchLocation,
          });

          return this.geocodingService.getByGoogle(query, shouldIncludeAddressDetails).pipe(
            switchMap((googleResults) => {
              if (googleResults && googleResults.length > 0) {
                const nonFrenchGoogleResults = googleResults.filter(
                  (result) => !result.name.toLowerCase().includes('france')
                );
                return of([...dataGouvResults, ...nonFrenchGoogleResults]);
              }
              return of(dataGouvResults);
            })
          );
        }

        return of(dataGouvResults);
      }),
      tap((results) => {
        if (!results || results.length === 0) {
          this.logsService.debug('StoreLocatorService', 'No locations found (DataGouv + Google)', { searchLocation });
        } else {
          this.logsService.debug(
            'StoreLocatorService',
            'Locations found (DataGouv / Google)',
            { searchLocation, count: results.length },
            results
          );
        }
      })
    );
  }

  /**
   * Resolves the coordinates of a suggestion, which must come from the latest {@link getLocation}
   * call: a Google one is resolved through the prediction that search kept.
   */
  resolveCoordinates(selected: LocalitySearch): Observable<GeoCoordinates | null> {
    return this.geocodingService.resolveCoordinates(selected);
  }
}
